/**
 * Approvals (P29 + P30 API wiring).
 *
 * Lifted from `sample-wireframe/client/src/pages/Home.tsx` §"ApprovalsPage"
 * (~lines 694–703). Now reads live data from `GET /api/v1/approvals` —
 * server-side state (pending / in_review / approved / rejected / withdrawn)
 * is mapped to the wireframe's 4-lane kanban shape.
 */
import { useMemo, type JSX } from 'react';
import { AlertTriangle, BadgeCheck } from 'lucide-react';

import { useApprovals, type ApprovalRow } from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { ApprovalItem } from '@/components/console/ApprovalItem.js';
import { ControlStrip, SelectControl } from '@/components/console/ControlStrip.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { ApprovalDetail } from '@/components/console/drilldowns/ApprovalDetail.js';
import { useUrlParam } from '@/hooks/useUrlState.js';

const LANES = ['Pending', 'In review', 'Decided', 'Withdrawn'] as const;
type LaneId = (typeof LANES)[number];

interface LaneItem {
  readonly id: string;
  readonly title: string;
  readonly requester: string;
  readonly risk: number;
  readonly type: string;
  readonly sla: string;
  readonly state: string;
  readonly lane: LaneId;
}

function laneFor(row: ApprovalRow): LaneId {
  if (row.state === 'withdrawn') return 'Withdrawn';
  if (row.state === 'approved' || row.state === 'rejected') return 'Decided';
  if (row.state === 'in_review') return 'In review';
  return 'Pending';
}

function riskScore(tier: string): number {
  return tier === 'critical' ? 95 : tier === 'high' ? 78 : tier === 'medium' ? 54 : 28;
}

function relativeSla(slaDueAt: string | null): string {
  if (!slaDueAt) return 'No SLA';
  const ms = Date.parse(slaDueAt) - Date.now();
  if (Number.isNaN(ms)) return slaDueAt;
  if (ms < 0) return 'Overdue';
  const hrs = Math.round(ms / 3_600_000);
  if (hrs < 1) return `${Math.round(ms / 60_000)}m`;
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

function rowToLaneItem(row: ApprovalRow): LaneItem {
  return {
    id: row.id,
    title: `${row.assetType} ${row.assetId}`,
    requester: row.requesterSub,
    risk: riskScore(row.riskTier),
    type: `${row.assetType} · ${row.riskTier} risk`,
    sla: relativeSla(row.slaDueAt),
    state: row.state,
    lane: laneFor(row),
  };
}

export default function Approvals(): JSX.Element {
  const approvals = useApprovals();
  const items: LaneItem[] = useMemo(
    () => (approvals.data?.items ?? []).map(rowToLaneItem),
    [approvals.data?.items],
  );
  const [selectedParam, setSelectedParam] = useUrlParam('approval', '');
  const selectedId = selectedParam || null;
  const setSelectedId = (id: string | null): void => setSelectedParam(id ?? '');
  const selected = useMemo(
    () => approvals.data?.items.find((a) => a.id === selectedId) ?? null,
    [approvals.data?.items, selectedId],
  );

  return (
    <>
      <PageHeader
        eyebrow="Govern"
        title="Approvals"
        description="A risk-scored queue for new servers, write tools, data exports, auth scopes, trigger endpoints, and policy exceptions."
      >
        <ActionButton icon={BadgeCheck}>Bulk approve low risk</ActionButton>
        <ActionButton icon={AlertTriangle} primary>
          Escalate critical
        </ActionButton>
      </PageHeader>

      <ControlStrip>
        <SelectControl
          label="Reviewer lane"
          options={['All lanes', 'Pending', 'In review', 'Decided', 'Withdrawn']}
        />
      </ControlStrip>

      {approvals.isError && (
        <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
          Approvals API unreachable. Hard-refresh; if it persists the gateway is down.
        </p>
      )}
      {approvals.data && (
        <p className="subtle" style={{ fontSize: 14, marginBottom: 16 }}>
          Live — {items.length} approval{items.length === 1 ? '' : 's'} in queue.
        </p>
      )}

      <div className="kanban-board">
        {LANES.map((lane) => {
          const laneItems = items.filter((a) => a.lane === lane);
          return (
            <div className="kanban-lane" key={lane}>
              <div className="lane-head">
                <h3>{lane}</h3>
                <span>{laneItems.length}</span>
              </div>
              {laneItems.length === 0 && (
                <p className="subtle" style={{ fontSize: 14 }}>
                  Empty
                </p>
              )}
              {laneItems.map((approval) => (
                <ApprovalItem
                  key={approval.id}
                  title={approval.title}
                  requester={approval.requester}
                  risk={approval.risk}
                  type={approval.type}
                  sla={approval.sla}
                  state={approval.state}
                  onClick={() => setSelectedId(approval.id)}
                />
              ))}
            </div>
          );
        })}
      </div>

      <ApprovalDetail
        open={selected != null}
        onClose={() => setSelectedId(null)}
        approval={selected}
      />
    </>
  );
}
